import { Suspense, lazy } from "react";

import Link from "next/link";

import { getRankingSelectDayLabelFromValue } from "pages/[tenant]/ranking/utils/selectDaysOptions";

import { ComponentErrorBoundary } from "shared/components/atoms/ComponentErrorBoundary";
import { GamificationModal } from "shared/components/molecules/Gamification/GamificationModal";
import {
  podiumImageMap,
  podiumPlaceName,
} from "shared/components/molecules/Gamification/utils/climbedPodiumModalUtils";
import { INotification } from "shared/components/organisms/AppNotification/@types";
import { IClimbedPodiumMetadata } from "shared/components/organisms/AppNotification/@types/climbedPodiumTypes";
import { ClimbedPodiumIcon } from "shared/components/organisms/AppNotification/assets/icons/ClimbedPodiumIcon";
import { Notification } from "shared/components/organisms/AppNotification/components/molecules/Notification";
import { formatTimeAgo } from "shared/components/organisms/AppNotification/utils/formatTimeAgo";
import { useTopbarContext } from "shared/providers/Layout/hooks/useTopbarContext";
import componentLoader from "shared/utils/componentLoader";

import { ClimbedPodiumNotificationContainer, SeeRankingButton } from "./styles";

const Confetti = lazy(() =>
  componentLoader(
    () =>
      import(
        /* webpackChunkFilename: 'Confetti', filename: 'Confetti' */
        "react-confetti"
      ),
  ),
);

interface IClimbedPodiumNotificationProps {
  notification: INotification<IClimbedPodiumMetadata>;
}

export const ClimbedPodiumNotification = ({
  notification,
}: IClimbedPodiumNotificationProps) => {
  const { notificationsIsOpen } = useTopbarContext();

  const podiumPlace = notification?.metadata?.current_position - 1;

  const podiumImage = podiumImageMap?.[podiumPlace];

  return (
    <ClimbedPodiumNotificationContainer podiumPlace={podiumPlace}>
      {notificationsIsOpen && (
        <ComponentErrorBoundary>
          <Suspense fallback={<></>}>
            <Confetti
              width={400}
              height={400}
              recycle={notificationsIsOpen}
              gravity={0.01}
            />
          </Suspense>
        </ComponentErrorBoundary>
      )}

      <Notification.Container wasReaded={!!notification?.read_at}>
        <Notification.IllustrationContainer>
          <ClimbedPodiumIcon />
        </Notification.IllustrationContainer>
        <div>
          <Notification.Title.Container>
            <Notification.Title.Highlight>
              Você subiu no pódio!
            </Notification.Title.Highlight>
          </Notification.Title.Container>

          <Notification.Content.Text>
            Parabéns por alcançar <b>{podiumPlaceName?.[podiumPlace]} lugar</b>{" "}
            no ranking, seus esforços podem ser vistos por todos!
          </Notification.Content.Text>

          <SeeRankingButton>
            <Link href={`/ranking`} prefetch={false}>
              Ver ranking
            </Link>
          </SeeRankingButton>

          <Notification.Footer.Container>
            <Notification.Footer.Text>
              {formatTimeAgo(notification?.created_at)}
            </Notification.Footer.Text>

            <Notification.Footer.Dot />

            <Notification.Footer.Text>
              {getRankingSelectDayLabelFromValue(
                notification?.metadata?.ranking_type,
              )}
            </Notification.Footer.Text>
          </Notification.Footer.Container>
        </div>

        <div className="level-container">
          <GamificationModal.UserPhoto
            fullName={notification?.metadata?.user?.display_name}
            photoSrc={notification?.metadata?.user?.photo_address}
            withStars
          />

          <div className="podium">
            <ComponentErrorBoundary>{podiumImage}</ComponentErrorBoundary>
          </div>
        </div>
      </Notification.Container>
    </ClimbedPodiumNotificationContainer>
  );
};
